import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { utilities } from "@shared/helpers/utilities";
import { useGuardedNavigate } from "@shared/hooks/useGuardedNavigate";
import { useModalContent } from "@shared/hooks/useModalContent";
import { useSecondaryNav } from "@shared/hooks/useSecondaryNav";
import { useUserProfile } from "@shared/hooks/useUserProfile";
import { useWindowSize } from "@shared/hooks/useWindowSize";

import ErrorBox from "@shared-components/errorBox/ErrorBox";

import { returnStringIfTrue } from "@app/helpers/componentHelpers";
import { useDeviceType } from "@app/hooks/useDeviceType";
import { useLastKnownPath } from "@app/hooks/useLastKnowPath";
import { PageTemplateProps } from "@app/types";

import { Box, Stack } from "@fermions";

import Breadcrumbs from "@components/atoms/Breadcrumbs/Breadcrumbs";
import AskHelp from "@components/molecules/AskHelp/AskHelp";
import Loading from "@components/molecules/Loading/Loading";
import ApplicationNotification from "@components/organisms/ApplicationNotification";
import Footer from "@components/organisms/Footer";
import Heading from "@components/organisms/Heading/Heading";
import SideMenu from "@components/organisms/SideMenu/SideMenu";

import PageBanner from "./PageBanner";
import PageHeader from "./PageHeader";
import { PageHeaderType } from "./PageHeader/PageHeader";
import PageModal from "./PageModal";
import "./PageTemplate.scss";
import SidePanel from "./SidePanel";
import variables from "./exportables.module.scss";
import { getBodySecondaryStyle } from "./pageTemplateHelper";

const SECONDARY_SIDEBAR_MIN_WIDTH = 400;

const PageTemplate: React.FC<PageTemplateProps> = ({
  header = {
    title: "",
    hasTitleTooltip: false,
    subtitle: "",
    subtitleActionIcon: undefined,
    sticky: false,
    actions: null,
    content: null,
    breadcrumbs: [],
    alwaysShowBreadcrumbs: false,
    tagline: ""
  },
  body = {
    header: null,
    primary: null,
    secondary: null,
    secondaryWidth: null,
    secondaryMaxWidth: null,
    secondaryMinWidth: null,
    secondaryOnRight: false
  },
  sidePanel = {
    open: false,
    content: null,
    width: null,
    hasSlider: false
  },
  modal = {
    open: false,
    content: null
  },
  other = {
    loading: null,
    error: null,
    help: null,
    unsaved: false,
    smallPageSize: 1400,
    isolationMode: false,
    hideFooter: false,
    fullScreenContent: false,
    project: undefined,
    client: undefined,
    secondaryNavDisabled: false,
    whiteSpaceStyle: "normal",
    pageReplacementMessage: ""
  },
  classNames = {
    page: "",
    banner: ""
  },
  skipSavePath = false,
  onPageScroll
}) => {
  useLastKnownPath(skipSavePath);
  const { i18n } = useTranslation();
  const location = useLocation();
  const windowSize = useWindowSize();
  const { userProfile } = useUserProfile();
  const { setUnsaved, getUnsavedPopup, handleNavigate } = useGuardedNavigate();
  const { width: secondaryNavWidth } = useSecondaryNav();
  const { isMobile } = useDeviceType();
  const {
    modalOpen,
    getModalContent,
    registerModal,
    handleOpenModal,
    handleCloseModal
  } = useModalContent();
  const [subNavActive, setSubNavActive] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [previousSidePanelWidth, setPreviousSidePanelWidth] = useState(
    SECONDARY_SIDEBAR_MIN_WIDTH + "px"
  );
  const [pageContentReplacement, setPageContentReplacement] =
    useState<React.ReactNode>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const bodyContentStyle = useMemo(() => {
    return {
      paddingTop: subNavActive ? variables.navigationBarHeight : 0
    };
    // Below line is to ensure the body content style updates with subNavActive update and header.title update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryNavWidth, header?.title, subNavActive, other.isolationMode]);

  const bodySecondaryStyle = useMemo(() => {
    return getBodySecondaryStyle(
      windowSize.width,
      other.smallPageSize,
      body.secondaryWidth,
      body.secondaryMaxWidth,
      body.secondaryMinWidth,
      variables.pageHeaderHeight,
      variables.navigationBarHeight,
      variables.bodyPadding
    );
  }, [
    windowSize.width,
    other.smallPageSize,
    body.secondaryWidth,
    body.secondaryMaxWidth,
    body.secondaryMinWidth
  ]);
  if (!sidePanel.width) {
    sidePanel.width = variables.sidePanelWidth;
  }

  const handleClearNotificationMessage = useCallback(() => {
    utilities.clearExternalLinkObjectFromSessionLastKnownPath();
  }, []);
  useEffect(() => {
    setUnsaved(other.unsaved || false);
  }, [other.unsaved, setUnsaved]);

  const transferPxToNum = useCallback((px: string): number => {
    return Number(px.substring(0, px.length - 2));
  }, []);
  const transferPxToVw = useCallback(
    (px: string): string => {
      return (transferPxToNum(px) / innerWidth) * 100 + "vw";
    },
    [transferPxToNum]
  );

  // This prevents body from scrolling when using page template as scrolling is handled in either body or side panel
  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  useEffect(() => {
    if (other.loading) {
      setPageContentReplacement(
        <Loading
          message={typeof other.loading === "string" ? other.loading : ""}
        />
      );
    } else if (other.help && other.help.length > 0) {
      setPageContentReplacement(<AskHelp message={other.help} />);
    } else if (
      other.pageReplacementMessage &&
      other.pageReplacementMessage.length > 0
    ) {
      setPageContentReplacement(
        <AskHelp message={other.pageReplacementMessage} iconName="" />
      );
    } else {
      setPageContentReplacement(null);
    }
  }, [other.loading, other.help, other.pageReplacementMessage]);

  useEffect(() => {
    const languagePreference =
      userProfile?.properties?.languagePreferences?.UILanguage;
    if (languagePreference && i18n.language !== languagePreference) {
      i18n.changeLanguage(languagePreference);
    }
  }, [userProfile, i18n, i18n.language]);

  useEffect(() => {
    if (location?.state?.externalLinkObject?.message) {
      setNotificationMessage(location.state.externalLinkObject.message);
    }
  }, [location?.state]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      const secondNavWidthVal = parseInt(secondaryNavWidth, 10);
      const leftNavEnable = !other.isolationMode && !other.secondaryNavDisabled;
      if (
        !isDragging ||
        window.innerWidth - e.x <= SECONDARY_SIDEBAR_MIN_WIDTH ||
        e.x <= (leftNavEnable ? secondNavWidthVal : 0)
      ) {
        return;
      }
      const newWidth = innerWidth - e.x;
      if (sidePanelRef.current?.style) {
        sidePanelRef.current.style.width = `${newWidth}px`;
      }
    },
    [
      isDragging,
      other.isolationMode,
      other.secondaryNavDisabled,
      secondaryNavWidth
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setPreviousSidePanelWidth(
        transferPxToVw(sidePanelRef.current?.style?.width || "")
      );
    }
    setIsDragging(false);
  }, [isDragging, transferPxToVw]);

  useEffect(() => {
    const container = containerRef.current;
    container?.addEventListener("mousemove", handleMouseMove);
    container?.addEventListener("mouseup", handleMouseUp);
    return () => {
      container?.removeEventListener("mousemove", handleMouseMove);
      container?.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    // Page Template
    <div
      id="page-template"
      data-testid="test-page-template"
      className={`${returnStringIfTrue(
        classNames?.page,
        classNames?.page
      )} page-template ${modal.open ? "page-template--modal-open" : ""}`}
      style={{
        flex: "auto",
        justifyContent: "space-between",
        width: "100vw"
      }}
    >
      {/* Header and Top Navigation */}
      {/* .main-page__header added for background image to apply */}
      <div
        id="page-template__header"
        className={`page-template__header main-page__header main-page__header${returnStringIfTrue(
          other.isolationMode,
          "--isolation"
        )}`}
      >
        <Heading
          subNavLeftPosition={secondaryNavWidth}
          setSubNavActive={setSubNavActive}
          isolationMode={other.isolationMode}
          handleNavigate={handleNavigate}
        />
      </div>
      {/* Body */}
      <div
        ref={containerRef}
        className={`page-template__body ${isDragging ? "dragging" : ""}`}
      >
        {/* Side Nav */}
        {!other.isolationMode && !other.secondaryNavDisabled && (
          <div
            className={returnStringIfTrue(
              "page-template__body__side-nav",
              "page-template__body__side-nav"
            )}
            style={{ width: secondaryNavWidth }}
          >
            <SideMenu
              handleNavigate={handleNavigate}
              project={other.project}
              client={other.client}
              width={secondaryNavWidth}
            />
          </div>
        )}
        {/* Content */}
        <Stack
          width="fill"
          style={{
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
            transition: "width 0s"
          }}
        >
          <PageBanner
            registerModal={registerModal}
            handleOpenModal={handleOpenModal}
            handleCloseModal={handleCloseModal}
          />
          {/* Breadcrumbs */}
          {((header?.breadcrumbs && header?.breadcrumbs?.length > 0) ||
            header?.alwaysShowBreadcrumbs) && (
            <Breadcrumbs
              breadcrumbs={header.breadcrumbs || []}
              currentPageTitle={header.title}
            />
          )}
          {header?.sticky && <PageHeader header={header as PageHeaderType} />}
          <div
            id="page-template__body__content"
            className={`page-template__body__content ${returnStringIfTrue(
              other.isolationMode,
              "page-template__body__content--isolation"
            )}`}
            style={bodyContentStyle}
            onScroll={onPageScroll}
          >
            {/* Heading */}
            {!header?.sticky && (
              <PageHeader header={header as PageHeaderType} />
            )}
            {/* Error message */}
            {other.error && (
              <div className={`page-template__body__content__error-message`}>
                <ErrorBox
                  type="component"
                  message={other.error}
                  whiteSpaceStyle={other.whiteSpaceStyle}
                />
              </div>
            )}
            {body.header && (
              <div className="page-template__body__content--body-header">
                {body.header}
              </div>
            )}
            <Stack
              height={isMobile ? "fit" : "100"}
              style={
                isMobile
                  ? {
                      position: "relative",
                      overflow: "auto"
                    }
                  : {}
              }
            >
              {header.content && (
                <Box
                  className="page-template__body__content__heading__content--mobile"
                  padding={isMobile ? "300" : "500"}
                  style={{
                    paddingBottom: "0px",
                    paddingTop: "var(--spacing-200)",
                    background:
                      "var(--components-atoms-page-title-context-light-color-background, #fff)"
                  }}
                >
                  <div className="page-template__body__content__heading__content">
                    {header.content}
                  </div>
                </Box>
              )}
              {/* Inner */}
              <div
                id="page-template__body__content__inner"
                className={`page-template__body__content__inner${returnStringIfTrue(
                  (windowSize.width || window.innerWidth) <
                    (other?.smallPageSize || 1400),
                  "--small-screen"
                )} ${returnStringIfTrue(
                  body.secondaryOnRight,
                  "page-template__body__content__inner--secondary-on-right"
                )} ${returnStringIfTrue(
                  other.fullScreenContent,
                  "page-template__body__content__inner--full-screen"
                )}`}
              >
                {/* Secondary content */}
                {body.secondary && (
                  <div
                    className={`page-template__body__content__inner__secondary`}
                    style={bodySecondaryStyle}
                  >
                    <div
                      className={`page-template__body__content__inner__secondary__content`}
                    >
                      {body.secondary}
                    </div>
                  </div>
                )}
                {/* Divider if needed */}
                {body.secondary && (
                  <div
                    className={`page-template__body__content__inner__divider`}
                  ></div>
                )}
                {/* Primary content */}
                {pageContentReplacement !== null ? (
                  pageContentReplacement && <>{pageContentReplacement}</>
                ) : (
                  <div
                    className={`page-template__body__content__inner__primary`}
                  >
                    {body.primary}
                  </div>
                )}
              </div>
              {/* Footer */}
              {!other.fullScreenContent && !other.hideFooter && (
                <div className={`page-template__body__content__footer`}>
                  <Footer />
                </div>
              )}
            </Stack>
          </div>
        </Stack>

        {/* Side panel */}
        <SidePanel
          ref={sidePanelRef}
          open={sidePanel?.open}
          hasSlider={sidePanel?.hasSlider}
          content={sidePanel?.content}
          subNavActive={subNavActive}
          isDragging={isDragging}
          onMouseDown={handleMouseDown}
          defaultWidth="400px"
          previousWidth={previousSidePanelWidth}
        />
        {/* Notification */}
        <ApplicationNotification
          type="error"
          title="Error"
          message={notificationMessage}
          clearMessage={handleClearNotificationMessage}
        />
        {/* Modal */}
        <PageModal
          content={modal?.content ?? null}
          open={modal?.open ?? false}
        />
        {modalOpen && (
          <PageModal content={getModalContent()} open={modalOpen} />
        )}
        {getUnsavedPopup()}
      </div>
    </div>
  );
};

export default PageTemplate;
